import React, { FC } from 'react';
import { Typography, IconButton } from '@material-ui/core';
import BackspaceIcon from '@mui/icons-material/Backspace';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/styles';

import impactAiSvg from 'assets/icons/impact-ai.svg';
import * as Colors from 'utils/color-styles';

interface ChatHeaderProps {
  isActionMode: boolean;
  onBackToPresets?: () => void;
  onClose?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Logo element - moves and scales
    logo: {
      position: 'absolute',
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      color: Colors.purple,
    },
    logoPreset: {
      top: '120px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 60,
      height: 30,
    },
    logoAction: {
      top: '22px', // Aligned with beta pill
      left: '24px',
      transform: 'translateX(0)',
      width: 40,
      height: 20,
    },

    // Text elements - fade in/out together
    textContainer: {
      position: 'absolute',
      top: '155px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(2),
      transition: 'opacity 0.3s ease-in-out',
    },
    textVisible: {
      opacity: 1,
    },
    textHidden: {
      opacity: 0,
    },
    logoText: {
      color: Colors.purple,
      fontWeight: 500,
    },
    helpText: {
      color: theme.palette.text.secondary,
      fontWeight: 400,
    },

    // Header bar with all buttons
    headerBar: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '64px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: '42px',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginLeft: theme.spacing(4), // Space for logo
      transition: 'opacity 0.3s ease-in-out 0.3s',
    },
    headerLeftVisible: {
      opacity: 1,
    },
    headerLeftHidden: {
      opacity: 0,
      pointerEvents: 'none',
    },

    betaPill: {
      backgroundColor: 'rgba(103, 58, 183, 0.1)',
      color: Colors.purple,
      border: `2px solid ${Colors.purple}`,
      borderRadius: theme.spacing(2),
      padding: theme.spacing(0.25, 1.25),
      display: 'flex',
      alignItems: 'center',
    },
    betaText: {
      fontWeight: 'bold',
      fontSize: '10px',
      letterSpacing: '1px',
    },
    backButton: {
      color: Colors.purple,
      padding: theme.spacing(0.5),
    },
    closeButton: {
      color: Colors.purple,
    },
  })
);

const ChatHeader: FC<ChatHeaderProps> = ({ isActionMode, onBackToPresets, onClose }) => {
  const classes = useStyles();

  return (
    <>
      {/* Logo - moves and scales */}
      <img
        src={impactAiSvg}
        className={`${classes.logo} ${isActionMode ? classes.logoAction : classes.logoPreset}`}
        alt="ImpactAi Logo"
      />

      {/* Text container - fades */}
      <div
        className={`${classes.textContainer} ${
          isActionMode ? classes.textHidden : classes.textVisible
        }`}
      >
        <Typography variant="h6" className={classes.logoText}>
          ImpactAi
        </Typography>
        <Typography variant="h6" className={classes.helpText}>
          How can I help you today?
        </Typography>
      </div>

      {/* Header bar with all buttons aligned */}
      <div className={classes.headerBar}>
        <div
          className={`${classes.headerLeft} ${
            isActionMode ? classes.headerLeftVisible : classes.headerLeftHidden
          }`}
        >
          <div className={classes.betaPill}>
            <Typography className={classes.betaText}>BETA</Typography>
          </div>
          {onBackToPresets && (
            <IconButton onClick={onBackToPresets} size="small" className={classes.backButton}>
              <BackspaceIcon />
            </IconButton>
          )}
        </div>

        {onClose && (
          <IconButton onClick={onClose} size="medium" className={classes.closeButton}>
            <HighlightOffIcon />
          </IconButton>
        )}
      </div>
    </>
  );
};

export default ChatHeader;
